// Thin fetch wrapper. All endpoints live under /api/* and rely on the session
// cookie set by /api/auth/login. We send `credentials: "include"` so cookies
// flow during dev (cross-port) as well as in production.

import { parsePackSize } from "../functions/lib/pack-size";
import type {
  BarcodeLookup,
  GroceryEntry,
  Household,
  InventoryEntry,
  InventoryLot,
  LowStockProduct,
  Product,
  User,
} from "./types";

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.headers as Record<string, string>),
  };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  const res = await fetch(path, {
    method,
    headers,
    body: payload,
    credentials: "include",
    ...init,
  });
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) || `Request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  // ---- auth ----
  signup: (body: {
    email: string;
    password: string;
    name: string;
    mode: "create" | "join";
    household_name?: string;
    invite_code?: string;
  }) => request<{ ok: true }>("POST", "/api/auth/signup", body),

  login: (email: string, password: string) =>
    request<{ ok: true }>("POST", "/api/auth/login", { email, password }),

  logout: () => request<{ ok: true }>("POST", "/api/auth/logout"),

  me: () =>
    request<{ user: User; household: Household }>("GET", "/api/auth/me"),

  // ---- household ----
  updateHousehold: (body: { name?: string; rotate_invite?: boolean }) =>
    request<{ household: Household }>("PATCH", "/api/household", body),

  householdMembers: () =>
    request<{ members: User[] }>("GET", "/api/household/members"),

  // ---- autocomplete suggestions ----
  getSuggestions: () =>
    request<{ locations: string[]; categories: string[] }>(
      "GET",
      "/api/suggestions"
    ),

  // ---- barcode lookup ----
  // Try OpenFoodFacts directly first. The worker proxy (/api/lookup/:barcode)
  // stays as a fallback for clients/networks that block the direct cross-origin
  // call or when OpenFoodFacts CORS is unavailable.
  //
  // Distinguishes "OpenFoodFacts answered, no such product" (resolve with
  // found:false → the UI says "type the name") from "couldn't reach
  // OpenFoodFacts" (rate limit / 5xx / 525 / timeout): the latter falls
  // through to the worker, which replies 502 so `request()` throws and the
  // caller can show "couldn't check, try again" instead of a misleading
  // "not found in any catalog".
  lookupBarcode: async (barcode: string): Promise<BarcodeLookup> => {
    try {
      const r = await fetch(
        `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(
          barcode
        )}.json`,
        { signal: AbortSignal.timeout(6000) }
      );
      if (r.ok) {
        const data = (await r.json()) as {
          status?: number;
          product?: {
            product_name?: string;
            brands?: string;
            image_front_url?: string;
            image_url?: string;
            categories?: string;
            quantity?: string;
          };
        };
        if (data.status === 1 && data.product) {
          const p = data.product;
          const pack = parsePackSize(p.quantity);
          return {
            barcode,
            found: true,
            name: p.product_name || undefined,
            brand: p.brands?.split(",")[0]?.trim() || undefined,
            image_url: p.image_front_url || p.image_url || undefined,
            category: p.categories?.split(",")[0]?.trim() || undefined,
            default_unit: pack.unit || undefined,
            pack_quantity: pack.quantity ?? undefined,
          };
        }
        // Reachable, no record — a genuine miss. Don't bother the worker.
        return { barcode, found: false };
      }
      // OpenFoodFacts v2 answers 404 for an unknown barcode — also a real miss.
      if (r.status === 404) return { barcode, found: false };
      // Any other non-2xx (rate limit, 5xx, 525 SSL handshake fail…) means we
      // couldn't actually check — fall through to the worker proxy below.
    } catch {
      // CORS / network / timeout — fall through to the worker proxy.
    }
    // The worker makes the same call; it returns 502 (→ thrown ApiError) if it
    // also can't reach OpenFoodFacts, and `{ found: false }` only for a real
    // "no such product" answer.
    return request<BarcodeLookup>(
      "GET",
      `/api/lookup/${encodeURIComponent(barcode)}`
    );
  },

  // ---- products ----
  listProducts: (q?: string) =>
    request<{ products: Product[] }>(
      "GET",
      `/api/products${q ? `?q=${encodeURIComponent(q)}` : ""}`
    ),

  getProductByBarcode: (barcode: string) =>
    request<{
      product: Product | null;
      last_location: string | null;
      // Quantity + unit of this product's most recent lot, so adding another
      // lot of a pack defaults to the pack size / unit again. Null when there
      // are no lots (or the last lot had no unit).
      last_quantity: number | null;
      last_unit: string | null;
    }>("GET", `/api/products/by-barcode/${encodeURIComponent(barcode)}`),

  getProduct: (id: string) =>
    request<{ product: Product; lots: InventoryLot[] }>(
      "GET",
      `/api/products/${id}`
    ),

  createProduct: (body: Partial<Product>) =>
    request<{ product: Product }>("POST", "/api/products", body),

  updateProduct: (id: string, body: Partial<Product>) =>
    request<{ product: Product }>("PATCH", `/api/products/${id}`, body),

  deleteProduct: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/products/${id}`),

  // ---- inventory ----
  listInventory: (params?: {
    q?: string;
    location?: string;
    expiring_within_days?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.location) qs.set("location", params.location);
    if (params?.expiring_within_days != null)
      qs.set("expiring_within_days", String(params.expiring_within_days));
    const s = qs.toString();
    return request<{ items: InventoryEntry[] }>(
      "GET",
      `/api/inventory${s ? `?${s}` : ""}`
    );
  },

  addInventory: (body: {
    product_id?: string;
    barcode?: string;
    name?: string;
    brand?: string | null;
    category?: string | null;
    default_unit?: string | null;
    image_url?: string | null;
    quantity?: number;
    unit?: string | null;
    best_before?: string | null;
    location?: string | null;
    notes?: string | null;
  }) => request<{ lot: InventoryLot }>("POST", "/api/inventory", body),

  getLot: (id: string) =>
    request<{ lot: InventoryLot }>("GET", `/api/inventory/${id}`),

  updateLot: (id: string, body: Partial<InventoryLot>) =>
    request<{ lot: InventoryLot }>("PATCH", `/api/inventory/${id}`, body),

  consumeLot: (id: string, amount = 1) =>
    request<{ ok: true; remaining: number; deleted: boolean }>(
      "POST",
      `/api/inventory/${id}/consume`,
      { amount }
    ),

  deleteLot: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/inventory/${id}`),

  // ---- low stock + grocery list ----
  listLowStock: () =>
    request<{ items: LowStockProduct[] }>("GET", "/api/low-stock"),

  listGrocery: (includeCompleted = false) =>
    request<{ items: GroceryEntry[] }>(
      "GET",
      `/api/grocery${includeCompleted ? "?include_completed=1" : ""}`
    ),

  addGroceryItem: (body: {
    product_id?: string | null;
    name?: string;
    quantity?: number;
    unit?: string | null;
    category?: string | null;
    notes?: string | null;
  }) => request<{ id: string }>("POST", "/api/grocery", body),

  updateGroceryItem: (
    id: string,
    body: {
      name?: string;
      quantity?: number;
      unit?: string | null;
      category?: string | null;
      notes?: string | null;
      completed?: boolean;
    }
  ) => request<{ ok: true }>("PATCH", `/api/grocery/${id}`, body),

  deleteGroceryItem: (id: string) =>
    request<{ ok: true }>("DELETE", `/api/grocery/${id}`),

  // ---- images ----
  uploadImage: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<{ image_url: string; key: string }>(
      "POST",
      "/api/images/upload",
      fd
    );
  },
};

export { ApiError };
